import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { warehouseV2Service } from '@/services'
import { ApiResponse } from '@/services/api'
import { CreateAreaRequest } from '@/services/WarehouseV2Service'
import { WarehouseArea } from '@/types/warehouse'

interface AreaState {
  loading: boolean
  error: string | null
  areas: WarehouseArea[]
  selectedArea: WarehouseArea | null
  creating: boolean
}

interface AreaActions {
  fetchAreas: () => Promise<void>
  createAreaWithPositions: (data: CreateAreaRequest) => Promise<ApiResponse>
  selectArea: (area: WarehouseArea | null) => void
  deleteArea: (id: number) => Promise<void>
  setError: (message: string) => void
  clearError: () => void
  refresh: () => Promise<void>
}

type AreaStore = AreaState & AreaActions

const initialState: AreaState = {
  loading: false,
  error: null,
  areas: [],
  selectedArea: null,
  creating: false
}

export const useAreaStore = create<AreaStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setError: (message: string) => set({ error: message }),
      clearError: () => set({ error: null }),

      // 获取所有库区
      fetchAreas: async () => {
        set({ loading: true, error: null })

        try {
          const { success, data } = await warehouseV2Service.getWarehouseAreas()
          if (success && data) {
            set({
              areas: data
            })
          }
        } finally {
          set({
            loading: false
          })
        }
      },

      // 创建库区并自动生成库位
      createAreaWithPositions: async (data: CreateAreaRequest) => {
        set({ creating: true, error: null })

        try {
          const { success, message } =
            await warehouseV2Service.createWarehouseArea(data)

          // 创建成功后需要重新获取库区列表，因为API只返回创建结果信息
          if (success) {
            await get().fetchAreas()
          }
          return { success, message }
        } finally {
          set({ creating: false })
        }
      },

      // 选择库区
      selectArea: (area: WarehouseArea | null) => {
        set({ selectedArea: area })
      },

      // 删除库区
      deleteArea: async (id: number) => {
        set({ loading: true, error: null })

        try {
          const { success } = await warehouseV2Service.deleteWarehouseArea(id)

          if (success) {
            // 更新本地状态
            const updatedAreas = get().areas.filter((area) => area.id !== id)
            set({ areas: updatedAreas })

            // 如果删除的是当前选中的库区，清空选择
            if (get().selectedArea?.id === id) {
              set({ selectedArea: null })
            }
          }
        } finally {
          set({ loading: false })
        }
      },

      // 刷新数据
      refresh: async () => {
        await Promise.all([get().fetchAreas()])
      }
    }),
    {
      name: 'area-store'
    }
  )
)

// 导出状态选择器
export const useAreaState = () => {
  const loading = useAreaStore((state) => state.loading)
  const error = useAreaStore((state) => state.error)
  const areas = useAreaStore((state) => state.areas)
  const selectedArea = useAreaStore((state) => state.selectedArea)
  const creating = useAreaStore((state) => state.creating)

  return {
    loading,
    error,
    areas,
    selectedArea,
    creating
  }
}

// 导出操作选择器
export const useAreaActions = () => {
  const fetchAreas = useAreaStore((state) => state.fetchAreas)
  const createAreaWithPositions = useAreaStore(
    (state) => state.createAreaWithPositions
  )
  const selectArea = useAreaStore((state) => state.selectArea)
  const deleteArea = useAreaStore((state) => state.deleteArea)
  const setError = useAreaStore((state) => state.setError)
  const clearError = useAreaStore((state) => state.clearError)
  const refresh = useAreaStore((state) => state.refresh)

  return {
    fetchAreas,
    createAreaWithPositions,
    selectArea,
    deleteArea,
    setError,
    clearError,
    refresh
  }
}
